import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PlatformBadge, PLATFORM_LABEL } from "@/components/Badges";
import { toast } from "sonner";
import { Wand2, CalendarClock, AlertTriangle, Rocket } from "lucide-react";

const todayPlus = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const AutopilotDialog = ({ open, onOpenChange, clientId, projectId, onApplied }) => {
  const [cfg, setCfg] = useState({
    start_date: todayPlus(1),
    horizon_days: 30,
    max_posts_per_day: 6,
    min_gap_hours: 3,
    skip_weekends: false,
    reschedule_all: false,
  });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const computePlan = async () => {
    setBusy(true);
    setPlan(null);
    try {
      const { data } = await api.post("/autopilot/plan", { client_id: clientId, project_id: projectId || null, ...cfg });
      setPlan(data);
      if (data.summary.total === 0) toast.info("Keine planbaren Posts gefunden (Status Entwurf/Geprüft/Bereit/Blockiert).");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const applyPlan = async () => {
    if (!plan?.assignments?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/autopilot/apply", {
        client_id: clientId,
        assignments: plan.assignments.map((a) => ({ post_id: a.post_id, scheduled_at: a.scheduled_at })),
      });
      toast.success(`${data.applied} Posts wurden terminiert & auf „Geplant“ gesetzt.`);
      onOpenChange(false);
      setPlan(null);
      onApplied?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setPlan(null); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="autopilot-sheet">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2"><Wand2 size={18} /> Kalender-Autopilot</SheetTitle>
          <SheetDescription>
            Ungeplante Posts automatisch auf beste Zeitfenster verteilen – mit Mindestabstand, Tageslimit und Plattform-Vorlauf (z.&nbsp;B. TikTok 24&nbsp;h).
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Startdatum</Label>
            <Input type="date" value={cfg.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="ap-start-date" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Zeitraum (Tage)</Label>
            <Input type="number" min={1} max={120} value={cfg.horizon_days} onChange={(e) => set("horizon_days", +e.target.value)} data-testid="ap-horizon" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Max. Posts / Tag</Label>
            <Input type="number" min={1} max={50} value={cfg.max_posts_per_day} onChange={(e) => set("max_posts_per_day", +e.target.value)} data-testid="ap-max-day" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Mindestabstand (Std.)</Label>
            <Input type="number" min={0} max={48} value={cfg.min_gap_hours} onChange={(e) => set("min_gap_hours", +e.target.value)} data-testid="ap-gap" className="mt-1" />
          </div>
          <div className="flex items-center justify-between col-span-2 border border-slate-200 rounded-md px-3 py-2">
            <div><div className="text-sm font-medium">Wochenenden überspringen</div><div className="text-xs text-slate-500">Sa/So werden nicht belegt</div></div>
            <Switch checked={cfg.skip_weekends} onCheckedChange={(v) => set("skip_weekends", v)} data-testid="ap-skip-weekends" />
          </div>
          <div className="flex items-center justify-between col-span-2 border border-slate-200 rounded-md px-3 py-2">
            <div><div className="text-sm font-medium">Alle neu planen</div><div className="text-xs text-slate-500">Auch bereits terminierte, offene Posts umverteilen</div></div>
            <Switch checked={cfg.reschedule_all} onCheckedChange={(v) => set("reschedule_all", v)} data-testid="ap-reschedule-all" />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button onClick={computePlan} disabled={busy} variant="outline" className="rounded-full" data-testid="ap-compute-btn">
            <CalendarClock size={15} className="mr-1.5" /> {busy && !plan ? "Berechne…" : "Vorschau berechnen"}
          </Button>
          {plan?.assignments?.length > 0 && (
            <Button onClick={applyPlan} disabled={busy} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold" data-testid="ap-apply-btn">
              <Rocket size={15} className="mr-1.5" /> {plan.assignments.length} Posts übernehmen
            </Button>
          )}
        </div>

        {plan && (
          <div className="mt-5" data-testid="ap-plan-result">
            <div className="grid grid-cols-3 gap-2 mb-4">
              <Stat label="Geplant" value={plan.summary.assigned} tone="emerald" />
              <Stat label="Ohne Slot" value={plan.summary.unassigned} tone="amber" />
              <Stat label="Gesamt" value={plan.summary.total} tone="slate" />
            </div>

            {Object.keys(plan.summary.by_platform).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(plan.summary.by_platform).map(([pf, n]) => (
                  <div key={pf} className="flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                    <PlatformBadge platform={pf} /> <span className="font-mono">{n}</span>
                  </div>
                ))}
              </div>
            )}

            {plan.assignments.length > 0 && (
              <div className="border border-slate-200 rounded-md overflow-hidden mb-4">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs text-slate-500 sticky top-0">
                      <tr><th className="px-3 py-2">Termin</th><th className="px-3 py-2">Plattform</th><th className="px-3 py-2">Serie</th><th className="px-3 py-2">Caption</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {plan.assignments.map((a) => (
                        <tr key={a.post_id} data-testid={`ap-row-${a.post_id}`}>
                          <td className="px-3 py-1.5 text-xs whitespace-nowrap font-mono">{new Date(a.scheduled_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                          <td className="px-3 py-1.5">{PLATFORM_LABEL[a.platform] || a.platform}</td>
                          <td className="px-3 py-1.5 font-mono text-xs">{a.series || "–"}</td>
                          <td className="px-3 py-1.5 max-w-[240px] truncate text-slate-600">{a.caption || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {plan.unassigned.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="ap-unassigned">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{plan.unassigned.length} Posts konnten nicht platziert werden. Erhöhe den Zeitraum oder das Tageslimit.</span>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Stat = ({ label, value, tone }) => {
  const tones = { emerald: "text-emerald-600", amber: "text-amber-600", slate: "text-slate-700" };
  return (
    <div className="border border-slate-200 rounded-md p-3 text-center">
      <div className={`text-2xl font-display font-bold ${tones[tone]}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
};
